import { type FieldPath } from 'react-hook-form';
import type { RefrigerationUnit } from '@/data/schemas';
import type { FieldValue } from '@/data/schemas/fieldPatterns.schemas';
import {
  isFieldValueCompatible,
  isFieldValueEmpty,
  normalizeFieldValue,
} from '@/data/fieldPatterns';
import type { RefrigerationUnitInput } from '@/data/repos/refrigerationUnitsRepo';
import { slugifyItalian } from '@/data/idGenerator';
import { newId } from '@/utils/ids';
import { toOptionalNumber, trim } from './shared';

export interface RefrigerationUnitCoreFormValues {
  name: string;
  location?: FieldValue;
  ammoniaKg?: number | null;
  toxicGasType?: FieldValue;
  impiantoType?: FieldValue;
  maintenanceContractorName?: string;
  notes?: string;
}

export class RefrigerationUnitCoreFormError extends Error {
  constructor(
    message: string,
    readonly field?: FieldPath<RefrigerationUnitCoreFormValues>
  ) {
    super(message);
    this.name = 'RefrigerationUnitCoreFormError';
  }
}

export const buildCoreDefaultValues = (
  refrigerationUnit?: RefrigerationUnit | null
): RefrigerationUnitCoreFormValues => {
  if (!refrigerationUnit) {
    return {
      name: '',
      location: undefined,
      ammoniaKg: null,
      toxicGasType: undefined,
      impiantoType: undefined,
      maintenanceContractorName: '',
      notes: '',
    } satisfies RefrigerationUnitCoreFormValues;
  }

  return {
    name: refrigerationUnit.name,
    location: refrigerationUnit.location ?? undefined,
    ammoniaKg: refrigerationUnit.ammoniaKg ?? null,
    toxicGasType: refrigerationUnit.toxicGasType ?? undefined,
    impiantoType: refrigerationUnit.impiantoType ?? undefined,
    maintenanceContractorName: refrigerationUnit.technical?.maintenanceContractor?.name ?? '',
    notes: refrigerationUnit.technical?.notes ?? '',
  } satisfies RefrigerationUnitCoreFormValues;
};

export const coreFormValuesToInput = (
  values: RefrigerationUnitCoreFormValues,
  options: { existingId?: string } = {}
): RefrigerationUnitInput => {
  if (values.name.trim().length === 0) {
    throw new RefrigerationUnitCoreFormError("Inserisci il nome dell'impianto", 'name');
  }

  let location = normalizeFieldValue(values.location) ?? undefined;
  if (!isFieldValueCompatible('open_enum', location)) {
    throw new RefrigerationUnitCoreFormError('Ubicazione impianto non valida', 'location');
  }
  if (isFieldValueEmpty(location)) {
    location = undefined;
  }

  const ammoniaKg = toOptionalNumber(values.ammoniaKg ?? undefined);

  let toxicGasType = normalizeFieldValue(values.toxicGasType) ?? undefined;
  if (!isFieldValueCompatible('closed_enum', toxicGasType)) {
    throw new RefrigerationUnitCoreFormError('Gas tossico non valido', 'toxicGasType');
  }
  if (isFieldValueEmpty(toxicGasType)) {
    toxicGasType = undefined;
  }

  let impiantoType = normalizeFieldValue(values.impiantoType) ?? undefined;
  if (!isFieldValueCompatible('open_enum', impiantoType)) {
    throw new RefrigerationUnitCoreFormError('Tipo impianto non valido', 'impiantoType');
  }
  if (isFieldValueEmpty(impiantoType)) {
    impiantoType = undefined;
  }

  const normalizedName = trim(values.name) ?? '';
  const slug = slugifyItalian(normalizedName);
  const refrigerationUnitId =
    options.existingId ?? (slug.length > 0 ? slug : `unit-${newId().slice(0, 8)}`);

  const maintenanceContractorName = trim(values.maintenanceContractorName);
  const notes = trim(values.notes);

  return {
    refrigerationUnitId,
    name: normalizedName,
    location,
    ammoniaKg,
    toxicGasType,
    impiantoType,
    technical: {
      maintenanceContractor: maintenanceContractorName
        ? { name: maintenanceContractorName }
        : undefined,
      notes,
    },
  } satisfies RefrigerationUnitInput;
};
